<script lang="ts" setup>
import type { Collections } from '@nuxt/content'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'
import { withLeadingSlash, joinURL, withoutTrailingSlash } from 'ufo'

const root = ref<HTMLElement>()
let media: gsap.MatchMedia | undefined

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger, SplitText)
  media = gsap.matchMedia()

  media.add('(prefers-reduced-motion: no-preference)', () => {
    const q = gsap.utils.selector(root.value)
    root.value!.classList.add('post-ready')

    // Long titles: revealed line by line rather than letter by letter.
    const title = SplitText.create(q('.post-title')[0], { type: 'lines', mask: 'lines' })
    gsap.timeline({ defaults: { ease: 'expo.out', lazy: false } })
      // The mask would keep clipping descenders and accents once the title is in place.
      .from(title.lines, { yPercent: 110, duration: 1, stagger: 0.09, onComplete: () => title.revert() }, 0)
      .from(q('.post-meta > *'), { y: 14, opacity: 0, duration: 0.6, stagger: 0.07, ease: 'power3.out', clearProps: 'all' }, 0.35)
      .from(q('.post-cover'), { clipPath: 'inset(10% 10% 10% 10% round 12px)', opacity: 0, duration: 1.2, clearProps: 'clipPath,opacity' }, 0.5)

    // How far through the article the reader is.
    gsap.from(q('.post-progress'), {
      scaleX: 0,
      ease: 'none',
      scrollTrigger: { trigger: q('.post-body')[0], start: 'top 70%', end: 'bottom bottom', scrub: 0.3 },
    })

    gsap.from(q('.post-next'), {
      y: 28,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: { trigger: q('.post-next')[0], start: 'top 90%', once: true },
    })

    return () => {
      title.revert()
      root.value?.classList.remove('post-ready')
    }
  }, root.value)
})

onBeforeUnmount(() => media?.revert())

const route = useRoute()
const { locale, t, localeProperties } = useI18n()

const slug = computed(() => {
  const params = route.params.slug
  if (!params) return []
  return Array.isArray(params) ? params : [params]
})
const path = computed(() => {
  const p = joinURL(locale.value, 'articles', ...slug.value)
  return withLeadingSlash(withoutTrailingSlash(p))
})
const collection = computed(() => `articles_${locale.value}` as keyof Collections)

const { data: page } = await useAsyncData(path.value, async () =>
  await queryCollection(collection.value).path(path.value).first() as Collections['articles_en'] | Collections['articles_fr'],
)

if (!page.value)
  throw createError({ statusCode: 404, statusMessage: 'Page not found' })

const { data: others } = await useAsyncData(`articles-next-${locale.value}`, async () =>
  await queryCollection(collection.value).all() as Collections['articles_en'][] | Collections['articles_fr'][],
)

// The next one in date order, looping back to the most recent after the oldest.
const nextArticle = computed(() => {
  const list = [...(others.value ?? [])].sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')))
  const index = list.findIndex(article => article.path === page.value?.path)
  return list.length > 1 ? list[(index + 1) % list.length] : undefined
})

const formattedDate = computed(() => {
  const date = page.value?.date
  if (!date) return ''
  const parsed = new Date(date)
  return Number.isNaN(parsed.getTime())
    ? date
    : new Intl.DateTimeFormat(locale.value, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(parsed)
})

const { copy } = useClipboard()

function copyArticleLink() {
  copy(`${window.location.origin}${route.fullPath}`)
  toast.success(t('global.article_link_copied'))
}

defineShortcuts({
  meta_shift_c: {
    usingInput: true,
    handler: () => {
      copy(`${window.location.origin}${route.fullPath}`)
      toast.success(t('global.article_link_copied'))
    },
  },
})

// OG images must be a raster format: the vector covers keep the site's default one.
if (page.value.image && !page.value.image.endsWith('.svg')) {
  defineOgImage({
    url: page.value.image,
  })
}
</script>

<template>
  <div
    v-if="page"
    ref="root"
    class="post-root"
  >
    <FolioMeta
      :page
      :is-writing="route.path.includes('/articles/')"
    />

    <!-- reading progress -->
    <div class="post-progress fixed inset-x-0 top-0 z-40 h-0.5 origin-left bg-[#b9c9ae]" />

    <div class="mx-auto max-w-7xl overflow-x-clip px-4 pb-16 sm:px-6 lg:px-8">
      <NuxtLinkLocale
        to="/writing"
        class="group mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/60 transition-colors hover:text-white sm:mt-16"
      >
        <UIcon
          name="lucide:arrow-left"
          class="size-4 transition-transform group-hover:-translate-x-1"
        />
        {{ $t("navigation.writing") }}
      </NuxtLinkLocale>

      <article>
        <header class="mt-8">
          <h1 class="post-in post-title max-w-6xl text-pretty text-[clamp(2.5rem,6.5vw,6rem)] font-semibold leading-[1.02] tracking-tighter text-white">
            {{ page.title }}
          </h1>
          <div class="post-in post-meta mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-white/15 pt-5 text-sm text-white/60">
            <time :datetime="page.date">{{ formattedDate }}</time>
            <span v-if="page.readingTime">{{ page.readingTime }}</span>
            <ul
              v-if="page.tags?.length"
              class="flex flex-wrap gap-2"
            >
              <li
                v-for="tag in page.tags"
                :key="tag"
                class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70"
              >
                {{ tag }}
              </li>
            </ul>
            <button
              type="button"
              class="inline-flex cursor-pointer items-center gap-1.5 font-medium text-white transition-opacity hover:opacity-70 sm:ml-auto"
              @click="copyArticleLink"
            >
              <UIcon
                name="lucide:link"
                class="size-4"
              />
              {{ $t("writing.share") }}
            </button>
          </div>
        </header>

        <div
          v-if="page.image"
          class="post-in post-cover mt-10 aspect-[16/8] overflow-hidden rounded-xl border border-white/10 bg-neutral-900 lg:mt-14"
        >
          <img
            :src="page.image"
            :alt="page.title"
            class="size-full object-cover"
          >
        </div>

        <div class="post-body mx-auto mt-14 max-w-3xl lg:mt-20">
          <ContentRenderer
            :dir="localeProperties?.dir ?? 'ltr'"
            :value="page"
          />
        </div>
      </article>

      <NuxtLink
        v-if="nextArticle"
        :to="nextArticle.path"
        class="post-next group mt-24 block border-t border-white/15 pt-8 lg:mt-32"
      >
        <span class="text-sm text-white/50">{{ $t("writing.read_next") }}</span>
        <span class="mt-3 flex items-center justify-between gap-6">
          <span class="text-balance text-[clamp(2rem,5vw,4.5rem)] font-semibold leading-[1.05] tracking-tight text-white">
            {{ nextArticle.title }}
          </span>
          <span class="flex size-14 shrink-0 items-center justify-center rounded-full bg-white text-neutral-950 transition-transform duration-500 group-hover:translate-x-1 group-hover:-rotate-45 sm:size-20">
            <UIcon
              name="heroicons:arrow-right"
              class="size-6 sm:size-8"
            />
          </span>
        </span>
      </NuxtLink>
    </div>
  </div>
</template>

<style>
  /* The article body comes from Markdown: same typography for every article. */
  .post-body {
    color: rgb(255 255 255 / 0.75);
  }

  .post-body > * > p,
  .post-body p {
    font-size: 1.125rem;
    line-height: 1.75;
  }

  .post-body h2 {
    margin-top: 3.5rem;
    font-size: clamp(1.75rem, 3vw, 2.25rem);
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: white;
  }

  .post-body li {
    font-size: 1.125rem;
    line-height: 1.7;
  }

  .post-body strong {
    font-weight: 600;
    color: white;
  }

  .post-body a {
    color: white;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* Hidden until the entrance starts, with a fallback in case the script never runs. */
  @media (prefers-reduced-motion: no-preference) {
    .post-root:not(.post-ready) .post-in {
      opacity: 0;
      animation: post-fallback 0.3s 2.5s forwards;
    }
  }

  @keyframes post-fallback {
    to {
      opacity: 1;
    }
  }
</style>
